import type { CSSProperties, ReactNode } from 'react'

export type PillTone =
  | 'info'
  | 'identity'
  | 'tracking'
  | 'blocking'
  | 'blocked-by'
  | 'warning'
  | 'danger'
  | 'neutral'
  | 'accent'
  | 'shared'

type PillVariant = 'pill' | 'chip'

const TONE: Record<PillTone, CSSProperties> = {
  info: { background: 'var(--info-label-bg)', color: 'var(--info-label)' },
  identity: { background: 'var(--identity-label-bg)', color: 'var(--identity-label)' },
  tracking: { background: 'var(--accent-soft)', color: 'var(--accent)' },
  blocking: { background: 'var(--rose-soft)', color: 'var(--rose)' },
  'blocked-by': { background: 'var(--warm-soft)', color: 'var(--warm)' },
  warning: { background: 'var(--warning-soft)', color: 'var(--warning)' },
  danger: { background: 'var(--danger-soft)', color: 'var(--danger)' },
  neutral: { background: 'var(--surface-3)', color: 'var(--text-2)' },
  accent: { background: 'var(--accent-soft)', color: 'var(--accent)' },
  shared: {
    background: 'linear-gradient(135deg,rgba(11,184,184,0.18),rgba(11,184,184,0.08))',
    color: 'var(--accent)',
    border: '1px solid rgba(11,184,184,0.22)',
  },
}

const VARIANT: Record<PillVariant, CSSProperties> = {
  // Soft rounded label (sans). Aurora+ `.pill`.
  pill: {
    fontFamily: 'var(--sans)',
    fontSize: 11,
    fontWeight: 500,
    padding: '4px 10px',
    borderRadius: 'var(--r-xs)',
  },
  // Compact mono tag used in rows/tables. Aurora+ `.chip` / `.ch-badge`.
  chip: {
    fontFamily: 'var(--mono)',
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '0.04em',
    padding: '2px 6px',
    borderRadius: 4,
  },
}

interface PillProps {
  tone?: PillTone
  variant?: PillVariant
  className?: string
  style?: CSSProperties
  children?: ReactNode
}

/**
 * Pill / chip — status badges used everywhere (Tracking, Blocked, −$8 gap,
 * SHARED BRAND). Token-driven; light/dark theme follows automatically.
 * Aurora+ source: `.pill`, `.chip`, `.ch-badge`, `.gap-pill` (s07 / s12).
 */
export function Pill({ tone = 'neutral', variant = 'pill', className, style, children }: PillProps) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
        ...VARIANT[variant],
        ...TONE[tone],
        ...style,
      }}
    >
      {children}
    </span>
  )
}
